import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';

import { getLocalizedErrorMessage } from '@features/assessment/utils/error-utils';
import { useTrans } from '@libs/i18n';

import { getSSORedirectUri, login } from '../api/auth';

export const useKeycloakOAuthCallback = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const handledCodeRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTrans();
  useEffect(() => {
    async function handleCallback() {
      const code = searchParams.get('code');
      const error = searchParams.get('error');

      if (error) {
        const errorMessage = getLocalizedErrorMessage(error, t, "Failed to login");

        setIsLoading(false);
        setSearchParams((params) => {
          params.delete('error');
          return params;
        }, { replace: true });
        enqueueSnackbar(errorMessage, {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          autoHideDuration: 6000,
        });
        return;
      }

      if (!code) {
        setIsLoading(false);
        return;
      }

      // Single-use guard: each Keycloak code may only be redeemed once.
      if (handledCodeRef.current === code) {
        return;
      }
      handledCodeRef.current = code;

      setIsLoading(true);
      // Must match the redirect_uri sent to Keycloak's /auth endpoint
      // (see SSO_REDIRECT_PATH in ../api/auth.ts), otherwise the token
      // exchange fails with invalid_grant.
      const redirectUri = getSSORedirectUri();

      // Strip OAuth params before awaiting so a reload mid-flight cannot
      // re-trigger redemption of the same single-use code.
      setSearchParams((params) => {
        params.delete('code');
        params.delete('session_state');
        params.delete('iss');
        return params;
      }, { replace: true });

      try {
        await login(code, redirectUri);
      } catch (err) {
        let errorMessage = 'Login failed';

        if (err instanceof AxiosError) {
          errorMessage = err.response?.data.error || 'Login failed';
        }

        enqueueSnackbar(errorMessage, {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          autoHideDuration: 6000,
        });
      } finally {
        setIsLoading(false);
      }
    }

    handleCallback();
  }, [searchParams, setSearchParams]);

  return { isLoading, setIsLoading };
};
